import { FetchCustomerData_api, FetchPaymentType_api } from "../../Api";
import { useQuery } from "@tanstack/react-query";
import { Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

const { Option } = Select;

export const PaymentMetaData = ({
  setAddedInquiyData,
  setAddedCustomeData,
  setAddedPaymentTypeData,
}) => {
  const [customerData, setCustomerData] = useState<any>([]);
  const [inquiryNumber, setInquiryNumber] = useState<any>([]);
  const [paymentTypeData, setPaymentTypeData] = useState<any>([]);

  const [initialCustomerData, setInitialCustomerData] = useState<any>([]);
  const [searchKeywordForCustomerSearch, setSearchKeywordForCustomerSearch] =
    useState("");
  const [debouncedQueryForCustomerSearch] = useDebounce(
    searchKeywordForCustomerSearch,
    500,
  );

  const { isFetching: isFetchingCustomers } = useQuery<any>({
    queryKey: ["initialCustomerList"],
    queryFn: async () => {
      const response: any = await FetchCustomerData_api(null, 1, 10);
      setInitialCustomerData(response.customerData || []);
      setCustomerData(response.customerData || []);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const handleSearch = async (keyword: string) => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) {
      setCustomerData(initialCustomerData);
      return;
    }

    const localMatches = initialCustomerData.filter(
      (c: any) =>
        c.name.toLowerCase().includes(trimmed) ||
        c.mobile.toLowerCase().includes(trimmed),
    );

    if (localMatches.length > 0) {
      setCustomerData(localMatches);
    } else {
      // If no local matches, we wait for debounce to trigger BE search
      // The current search keyword will be captured by debouncedQuery
    }
  };

  const { isFetching: isFetchingPaymentType } = useQuery<any>({
    queryKey: ["paymentTypeData"],
    queryFn: async () => {
      const response: any = await FetchPaymentType_api();
      setPaymentTypeData(response.paymentType);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const trimmed = debouncedQueryForCustomerSearch.trim().toLowerCase();
    if (!trimmed) {
      setCustomerData(initialCustomerData);
      return;
    }

    const localMatches = initialCustomerData.filter(
      (c: any) =>
        c.name.toLowerCase().includes(trimmed) ||
        c.mobile.toLowerCase().includes(trimmed),
    );

    if (localMatches.length === 0) {
      const fetchFromBE = async () => {
        const response: any = await FetchCustomerData_api(trimmed, 1, 10);
        setCustomerData(response.customerData || []);
      };
      fetchFromBE();
    }
  }, [debouncedQueryForCustomerSearch]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl">
        {/* Customer */}
        <div className="flex flex-col">
          <label className="block text-sm mb-1 text-gray-800">Customer</label>
          <Select
            showSearch
            loading={isFetchingCustomers}
            placeholder="Select a customer"
            optionFilterProp="children"
            className="w-full"
            onSearch={(value: string) => {
              setSearchKeywordForCustomerSearch(value);
              handleSearch(value);
            }}
            onChange={(value) => {
              const selectedCustomer = customerData?.find(
                (customer) => customer.id === value,
              );
              setAddedCustomeData(selectedCustomer);
              setInquiryNumber(selectedCustomer?.inquiries || []);
            }}
            notFoundContent={
              isFetchingCustomers ? (
                <Spin size="small" />
              ) : searchKeywordForCustomerSearch ? (
                <Spin size="small" />
              ) : customerData?.length === 0 ? (
                <div className="px-2 py-1 text-gray-500">No data found</div>
              ) : null
            }
          >
            {isFetchingCustomers ? (
              <Spin size="small" />
            ) : (
              <>
                <Option key="all" value="">
                  All customers
                </Option>
                {customerData?.map((data) => (
                  <Option key={data.id} value={data.id}>
                    {data.name} - {data.mobile}
                  </Option>
                ))}
              </>
            )}
          </Select>
        </div>

        {/* Payment Type */}
        <div className="flex flex-col">
          <label className="block text-sm mb-1 text-gray-800">
            Payment Type
          </label>
          <Select
            loading={isFetchingPaymentType}
            placeholder="Select payment type"
            className="w-full"
            onChange={(value) => {
              const selectedPaymentType = paymentTypeData?.find(
                (type) => type.id === value,
              );
              setAddedPaymentTypeData(selectedPaymentType);
            }}
          >
            {paymentTypeData?.map((data, idx) => (
              <Option key={idx} value={data.id}>
                {data.type}
              </Option>
            ))}
          </Select>
        </div>

        {/* Inquiry Number */}
        <div className="flex flex-col">
          <label className="block text-sm mb-1 text-gray-800">
            Inquiry Number
          </label>
          <Select
            showSearch
            placeholder="Select or type an inquiry number"
            className="w-full"
            onChange={(value) => {
              const selectedInquiry = inquiryNumber?.find(
                (item) => item.id === value,
              );
              setAddedInquiyData(selectedInquiry);
            }}
          >
            {inquiryNumber?.map((data, idx) => (
              <Option key={idx} value={data.id}>
                {data.inquiryNumber}
              </Option>
            ))}
          </Select>
        </div>
      </div>
    </>
  );
};
